import React, {useContext} from 'react';
import { Box, Typography, Stack, Grid, useMediaQuery } from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import localStrings from "../localStrings";
import moment from "moment";
import useAuth from "../hooks/useAuth";
import { filterWeek, filterMonth, filterSingleDay } from "../pages/Home/FilterInfoOverview";

function ComparisonBlock({
    currentValue,
    previousValue,
    label,
    formatter,
    showPercentage = true,
    invertColors = false,
    isMobile = false
}) {
    // Check if we have valid previous data (null/undefined means no data, 0 is valid data)
    const hasValidPreviousData = previousValue !== null && previousValue !== undefined;

    if (!hasValidPreviousData) {
        return (
            <Box sx={{
                p: isMobile ? 1 : 1.5,
                borderRadius: 1,
                backgroundColor: (theme) => theme.palette.mode === 'dark'
                    ? 'background.paper'
                    : 'grey.50',
                border: 1,
                borderColor: 'divider',
                width: '100%',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center'
            }}>
                <Stack spacing={isMobile ? 0.5 : 1}>
                    <Typography variant={isMobile ? "caption" : "caption"} color="text.secondary" fontWeight="medium" fontSize={isMobile ? "0.7rem" : undefined}>
                        {label}
                    </Typography>
                    <Typography variant={isMobile ? "caption" : "body2"} color="text.secondary" sx={{ textAlign: 'center' }}>
                        {localStrings.noData}
                    </Typography>
                </Stack>
            </Box>
        );
    }

    // Calculate difference and percentage
    const difference = currentValue - previousValue;
    const percentageChange = previousValue !== 0
        ? ((difference / previousValue) * 100).toFixed(1)
        : currentValue > 0 ? "+100" : currentValue < 0 ? "-100" : "0"; // When going from 0 to a value

    // Determine trend
    const isPositive = difference > 0;
    const isNegative = difference < 0;
    const isFlat = difference === 0;

    // Determine color based on trend and inversion
    const getTrendColor = () => {
        if (isFlat) return 'text.secondary';
        if (invertColors) {
            return isPositive ? 'error.main' : 'success.main';
        }
        return isPositive ? 'success.main' : 'error.main';
    };

    // Get appropriate icon
    const getTrendIcon = () => {
        if (isFlat) return <Typography variant="body2" component="span" sx={{ fontWeight: 'bold', fontSize: isMobile ? '1rem' : '1.2rem' }}>=</Typography>;
        if (isPositive) return <TrendingUpIcon fontSize="small" />;
        return <TrendingDownIcon fontSize="small" />;
    };

    // Format the difference value
    const formattedDifference = formatter
        ? formatter(Math.abs(difference))
        : Math.abs(difference).toLocaleString();

    const trendColor = getTrendColor();

    return (
        <Box sx={{
            p: isMobile ? 1 : 1.5,
            borderRadius: 1,
            backgroundColor: (theme) => theme.palette.mode === 'dark'
                ? 'background.paper'
                : 'grey.50',
            border: 1,
            borderColor: 'divider',
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center'
        }}>
            <Stack spacing={isMobile ? 0.5 : 1}>
                <Typography variant="caption" color="text.secondary" fontWeight="medium" fontSize={isMobile ? "0.7rem" : undefined}>
                    {label}
                </Typography>

                <Stack direction={isMobile ? "column" : "row"} alignItems={isMobile ? "flex-start" : "center"} spacing={isMobile ? 0.2 : 0.5}>
                    <Box sx={{
                        color: trendColor,
                        display: 'flex',
                        alignItems: 'center',
                        gap: isMobile ? 0.3 : 0.5
                    }}>
                        {getTrendIcon()}
                        {!isFlat && (
                            <Typography variant={isMobile ? "h6" : "body1"} fontWeight="bold" fontSize={isMobile ? "1.1rem" : undefined}>
                                {isPositive ? '+' : ''}
                                {formattedDifference}
                            </Typography>
                        )}
                    </Box>
                    {showPercentage && !isFlat && (
                        <Typography variant={isMobile ? "caption" : "body2"} color="text.secondary" fontSize={isMobile ? "0.8rem" : undefined}>
                            ({percentageChange}%)
                        </Typography>
                    )}
                </Stack>
            </Stack>
        </Box>
    );
}

function DualPeriodComparison({
    currentValue,
    previousDayValue,
    previousWeekValue,
    previousMonthValue,
    previousYearValue,
    currentDate,
    formatter,
    showPercentage = true,
    invertColors = false,
    showMonthComparison = false, // New prop to enable month comparison
    comparisonMode = null // Auto-determine from filter if not provided
}) {
    const {filter} = useAuth();

    const isMobile = useMediaQuery((theme) => theme.breakpoints.down('md'));

    // Auto-determine comparison mode based on filter type
    const getComparisonMode = () => {
        if (comparisonMode) return comparisonMode; // Use explicit mode if provided

        if (filter?.filterType === filterSingleDay) {
            return 'day-week'; // Single day view: compare with previous day and same day last week
        } else if (filter?.filterType === filterWeek) {
            return 'week-month'; // Week view: compare with previous week and same week last month
        } else if (filter?.filterType === filterMonth) {
            return 'month-year'; // Month view: compare current month with previous month and same month last year
        }

        return 'day-week'; // Default fallback
    };

    const activeComparisonMode = getComparisonMode();

    // Convertir currentDate en moment de manière fiable (gère Firebase Timestamp, dayjs, Date, string)
    const getCurrentMoment = () => {
        if (!currentDate) return moment();
        if (currentDate.toDate && typeof currentDate.toDate === 'function') {
            return moment(currentDate.toDate()); // Firebase Timestamp
        }
        if (currentDate.$d) {
            return moment(currentDate.$d); // dayjs object
        }
        return moment(currentDate); // Date, string, ou autre
    };

    // Get day names for labels
    const getCurrentDayName = () => {
        const dayIndex = getCurrentMoment().day(); // 0 = Sunday, 1 = Monday, etc.
        const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
        return localStrings.dayNames[dayNames[dayIndex]];
    };

    const getPreviousDayName = () => {
        const dayIndex = getCurrentMoment().subtract(1, 'day').day();
        const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
        return localStrings.dayNames[dayNames[dayIndex]];
    };

    // Helper function to render comparison blocks based on mode
    const renderComparisons = () => {
        switch (activeComparisonMode) {
            case 'week-month':
                return (
                    <>
                        <Grid item xs={6} sm={6} sx={{ display: 'flex' }}>
                            <ComparisonBlock
                                currentValue={currentValue}
                                previousValue={previousWeekValue}
                                label={isMobile ? localStrings.vsLastWeek : localStrings.vsPreviousWeek}
                                formatter={formatter}
                                showPercentage={showPercentage}
                                invertColors={invertColors}
                                isMobile={isMobile}
                            />
                        </Grid>

                        <Grid item xs={6} sm={6} sx={{ display: 'flex' }}>
                            <ComparisonBlock
                                currentValue={currentValue}
                                previousValue={previousMonthValue}
                                label={isMobile ? localStrings.vsSameWeekLastMonthShort : localStrings.vsSameWeekLastMonth}
                                formatter={formatter}
                                showPercentage={showPercentage}
                                invertColors={invertColors}
                                isMobile={isMobile}
                            />
                        </Grid>
                    </>
                );

            case 'day-month':
                return (
                    <>
                        <Grid item xs={6} sm={6} sx={{ display: 'flex' }}>
                            <ComparisonBlock
                                currentValue={currentValue}
                                previousValue={previousDayValue}
                                label={isMobile ? localStrings.vsYesterday : `${getCurrentDayName()} vs ${getPreviousDayName()}`}
                                formatter={formatter}
                                showPercentage={showPercentage}
                                invertColors={invertColors}
                                isMobile={isMobile}
                            />
                        </Grid>

                        <Grid item xs={6} sm={6} sx={{ display: 'flex' }}>
                            <ComparisonBlock
                                currentValue={currentValue}
                                previousValue={previousMonthValue}
                                label={isMobile ? localStrings.vsLastMonth : localStrings.vsPreviousMonth}
                                formatter={formatter}
                                showPercentage={showPercentage}
                                invertColors={invertColors}
                                isMobile={isMobile}
                            />
                        </Grid>
                    </>
                );

            case 'month-year':
                return (
                    <>
                        <Grid item xs={6} sm={6} sx={{ display: 'flex' }}>
                            <ComparisonBlock
                                currentValue={currentValue}
                                previousValue={previousMonthValue}
                                label={isMobile ? localStrings.vsPrevMonthShort : localStrings.vsPrevMonth}
                                formatter={formatter}
                                showPercentage={showPercentage}
                                invertColors={invertColors}
                                isMobile={isMobile}
                            />
                        </Grid>

                        <Grid item xs={6} sm={6} sx={{ display: 'flex' }}>
                            <ComparisonBlock
                                currentValue={currentValue}
                                previousValue={previousYearValue}
                                label={isMobile ? localStrings.vsPrevYear : localStrings.vsSameMonthLastYear}
                                formatter={formatter}
                                showPercentage={showPercentage}
                                invertColors={invertColors}
                                isMobile={isMobile}
                            />
                        </Grid>
                    </>
                );

            case 'day-week':
            default:
                return (
                    <>
                        <Grid item xs={6} sm={6} sx={{ display: 'flex' }}>
                            <ComparisonBlock
                                currentValue={currentValue}
                                previousValue={previousDayValue}
                                label={isMobile ? localStrings.vsYesterday : `${getCurrentDayName()} vs ${getPreviousDayName()}`}
                                formatter={formatter}
                                showPercentage={showPercentage}
                                invertColors={invertColors}
                                isMobile={isMobile}
                            />
                        </Grid>

                        <Grid item xs={6} sm={6} sx={{ display: 'flex' }}>
                            <ComparisonBlock
                                currentValue={currentValue}
                                previousValue={previousWeekValue}
                                label={isMobile ? localStrings.vsLastWeek : `${getCurrentDayName()} vs ${getCurrentDayName()} dernière sem.`}
                                formatter={formatter}
                                showPercentage={showPercentage}
                                invertColors={invertColors}
                                isMobile={isMobile}
                            />
                        </Grid>
                    </>
                );
        }
    };

    return (
        <Box sx={{
            p: isMobile ? 1 : 2,
            borderBottom: 1,
            borderColor: 'divider',
            backgroundColor: (theme) => theme.palette.mode === 'dark'
                ? 'background.default'
                : 'background.paper'
        }}>
            <Grid container spacing={isMobile ? 1 : 2} sx={{ alignItems: 'stretch' }}>
                {renderComparisons()}
            </Grid>
        </Box>
    );
}

export default DualPeriodComparison;